// ============================================================================
// cg-measures.ts — mesures géométriques en direct (longueurs, angles) pour
// l'affichage élève d'un bloc `constrained_geometry`. PUR & testable : opère
// sur les positions RÉSOLUES des points (CGPoint[]), pas sur le bloc brut.
// ============================================================================

import type { CGPoint } from './visual-blocks';

interface XY {
  x: number;
  y: number;
}

function indexById(points: CGPoint[]): Map<string, XY> {
  const m = new Map<string, XY>();
  for (const p of points) m.set(p.id, { x: p.x, y: p.y });
  return m;
}

/** Longueur |AB|. `null` si un point est introuvable. */
export function measureSegment(points: CGPoint[], aId: string, bId: string): number | null {
  const m = indexById(points);
  const a = m.get(aId);
  const b = m.get(bId);
  if (!a || !b) return null;
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Mesure de l'angle ∠(a, vertex, b) en DEGRÉS, dans [0, 180]. `null` si un point
 * est introuvable ou si un côté est dégénéré (sommet confondu avec a ou b).
 */
export function measureAngleDeg(
  points: CGPoint[],
  aId: string,
  vertexId: string,
  bId: string,
): number | null {
  const m = indexById(points);
  const a = m.get(aId);
  const v = m.get(vertexId);
  const b = m.get(bId);
  if (!a || !v || !b) return null;
  const u1x = a.x - v.x;
  const u1y = a.y - v.y;
  const u2x = b.x - v.x;
  const u2y = b.y - v.y;
  const n1 = Math.hypot(u1x, u1y);
  const n2 = Math.hypot(u2x, u2y);
  if (n1 < 1e-9 || n2 < 1e-9) return null;
  // cos clampé pour éviter NaN d'acos sur des arrondis hors [-1, 1].
  const cos = Math.max(-1, Math.min(1, (u1x * u2x + u1y * u2y) / (n1 * n2)));
  return (Math.acos(cos) * 180) / Math.PI;
}

/** Arrondi d'affichage (1 décimale par défaut), pour les libellés de mesure. */
export function formatMeasure(value: number, digits = 1): string {
  return value.toFixed(digits);
}
